/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  THE ONE PHONE NORMALISATION FUNCTION (FR-5.8.3, FR-5.8.3a).
 *
 *  The UI, the API and the bulk importer all call THIS function. If the browser strips
 *  differently from the server, uniqueness breaks in a way that presents as an
 *  intermittent login bug — someone can register but not sign in, and only sometimes.
 *  That is why it lives in `packages/shared` and why there is exactly one of it.
 *
 *  Phones are stored as EXACTLY 10 DIGITS. No country code, no `+`, no separators.
 *  The country code is added at the SMS delivery boundary and nowhere else (FR-5.8.3c).
 * ─────────────────────────────────────────────────────────────────────────────────
 */

export const PHONE_REJECTION = {
  /** Blank or whitespace only. "Required" is a different correction from "invalid". */
  EMPTY: 'EMPTY',
  /** Non-blank, but nothing survived the strip — `"+"`, `"()"`, `"- -"`. */
  NO_DIGITS: 'NO_DIGITS',
  /** A character that is neither a digit nor a listed separator. */
  NON_NUMERIC: 'NON_NUMERIC',
  TOO_SHORT: 'TOO_SHORT',
  TOO_LONG: 'TOO_LONG',
} as const;

export type PhoneRejection = (typeof PHONE_REJECTION)[keyof typeof PHONE_REJECTION];

export type PhoneResult =
  /** `phone` is exactly 10 ASCII digits. */
  | { ok: true; phone: string }
  /** `digits` is the post-strip value, so the UI can say what it actually read. */
  | { ok: false; reason: PhoneRejection; digits?: string };

/**
 * Whitespace, hyphens (including the unicode dashes Word and Excel substitute),
 * brackets and dots. Everything else that is not a digit is a REJECTION, never a strip:
 * silently deleting a letter would turn `98765abc43` into a phone number the user never
 * typed, and FR-5.8.3 says "never silently truncated".
 */
const SEPARATORS = /[\s\-().[\]‐-―−]/g;

/**
 * ASCII digits only, deliberately. `\d` under the `u` flag or `\p{Nd}` would accept
 * `९८७६५४३२१०` and normalise it to something the server and a Python importer would
 * disagree about. Silent coercion is the failure mode this whole file exists to avoid.
 */
const TEN_DIGITS = /^[0-9]{10}$/;
const ALL_DIGITS = /^[0-9]+$/;

/** No legitimate input is longer than this; it bounds the loop below against abuse. */
const MAX_RAW_LENGTH = 32;

export const normalisePhone = (raw: string): PhoneResult => {
  if (typeof raw !== 'string' || raw.trim().length === 0) {
    return { ok: false, reason: PHONE_REJECTION.EMPTY };
  }
  if (raw.length > MAX_RAW_LENGTH) {
    return { ok: false, reason: PHONE_REJECTION.TOO_LONG };
  }

  // (a) Strip separators, then a LEADING '+' only. A '+' anywhere else is not a
  //     country-code marker, it is a malformed number, and it falls through to (c).
  let value = raw.replace(SEPARATORS, '');
  if (value.startsWith('+')) value = value.slice(1);

  if (value.length === 0) return { ok: false, reason: PHONE_REJECTION.NO_DIGITS };

  /**
   * (b) IF THE RESULT IS EXACTLY 10 DIGITS, STOP.
   *
   * This must come BEFORE the prefix stripping in (c), and it is not stylistic. Indian
   * mobile numbers begin 6–9, so `9198765432` is a perfectly valid 10-digit number that
   * happens to start `91`. Strip first and you silently corrupt it into 8 digits —
   * and the officer it belongs to can never receive an OTP again.
   *
   * Tested with `/^[0-9]{10}$/` and never `value.length === 10`, which would accept
   * `98765abc43` — ten characters, not ten digits.
   */
  if (TEN_DIGITS.test(value)) return { ok: true, phone: value };

  if (!ALL_DIGITS.test(value)) {
    return { ok: false, reason: PHONE_REJECTION.NON_NUMERIC, digits: value };
  }

  // (c) Reduce: while longer than 10, remove a leading '0' or a leading '91'.
  while (value.length > 10) {
    if (value.startsWith('0')) {
      value = value.slice(1);
      continue;
    }
    if (value.startsWith('91')) {
      value = value.slice(2);
      continue;
    }
    /**
     * WITHOUT THIS BREAK THIS LOOP NEVER TERMINATES.
     *
     * `9198765432109876` is 16 digits: strip `91` twice and you reach 12, then 14→12→
     * a value starting with neither `0` nor `91`. With no exit the request thread spins
     * forever, holding a connection, with nothing in the logs.
     */
    break;
  }

  // (d) Accept only at exactly 10. Anything else is rejected with a reason, never truncated.
  if (TEN_DIGITS.test(value)) return { ok: true, phone: value };

  return {
    ok: false,
    reason: value.length > 10 ? PHONE_REJECTION.TOO_LONG : PHONE_REJECTION.TOO_SHORT,
    digits: value,
  };
};

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C4a §2] IS THIS SEARCH TERM A PHONE NUMBER? — and if so, a whole one or a part.
 *
 *  In THIS file because the separators and the ASCII-digit rule are this file's, and a second
 *  copy of "what counts as a phone" in a search helper is how the two would drift.
 *
 *  ⚠️ A FLOOR OF THREE DIGITS. Below it every officer in a unit matches, which is the unbounded
 *  read the directory's three-character minimum exists to prevent — and `"+9 1"` passes a
 *  character count while reducing to `91`. A shorter term keeps the name (and GL) search.
 *
 *  ⚠️ A WHOLE NUMBER IS `exact`, NEVER A REGEX. `normalisePhone` succeeding means the term IS ten
 *  digits once `+91`/`0` are dropped, so it is one equality lookup on the unique `phone_1` index.
 *  Only a part is a substring.
 *
 *  ⚠️ A LEADING `+91` IS DROPPED FROM A PART TOO. `+91 98765` means "98765…", and searching the
 *  digits `9198765` would find nobody. Only after a `+`: a bare `91987` is a number that genuinely
 *  begins 9-1, which is `normalisePhone`'s own step (b) warning.
 *
 *  ⚠️ MATCHING IS NOT DISCLOSING. This decides what a query matches; what a surface RENDERS is the
 *  surface's decision and does not move because a number can now be searched.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const PHONE_SEARCH_MIN_DIGITS = 3;

export type PhoneSearch = { kind: 'exact'; phone: string } | { kind: 'partial'; digits: string };

export const phoneSearchOf = (term: string): PhoneSearch | null => {
  if (typeof term !== 'string' || term.length > MAX_RAW_LENGTH) return null;

  const trimmed = term.trim();
  let value = trimmed.replace(SEPARATORS, '');
  const hadPlus = value.startsWith('+');
  if (hadPlus) value = value.slice(1);
  if (!ALL_DIGITS.test(value)) return null;

  const whole = normalisePhone(trimmed);
  if (whole.ok) return { kind: 'exact', phone: whole.phone };

  if (hadPlus && value.startsWith('91')) value = value.slice(2);
  return value.length >= PHONE_SEARCH_MIN_DIGITS ? { kind: 'partial', digits: value } : null;
};

/**
 * The human-readable reason. FR-5.8.3b: "Rejection states what was wrong rather than
 * clearing the field", so these say what was actually read, not just "invalid".
 *
 * The UI renders this string and so does the API's 422, because both call this function —
 * the officer and the developer see the same words.
 */
export const describePhoneRejection = (result: Extract<PhoneResult, { ok: false }>): string => {
  const count = result.digits?.length ?? 0;

  switch (result.reason) {
    case PHONE_REJECTION.EMPTY:
      return 'Phone number is required.';
    case PHONE_REJECTION.NO_DIGITS:
      return 'That contains no digits. Enter a 10-digit mobile number.';
    case PHONE_REJECTION.NON_NUMERIC:
      return 'That contains characters that are not digits. Enter a 10-digit mobile number.';
    case PHONE_REJECTION.TOO_SHORT:
      return `That reduces to ${count} digit${count === 1 ? '' : 's'}. A mobile number is 10 digits.`;
    case PHONE_REJECTION.TOO_LONG:
      return count > 0
        ? `That reduces to ${count} digits and does not start with 0 or 91. A mobile number is 10 digits.`
        : 'That is too long to be a mobile number. A mobile number is 10 digits.';
  }
};

/** True for a value already in canonical storage form. Cheap guard at a model boundary. */
export const isNormalisedPhone = (value: string): boolean => TEN_DIGITS.test(value);

/**
 * For logs and seeder output. NFR §7: no officer PII in logs, and a phone number is PII.
 * Keeps enough to recognise a number you already know, not enough to learn a new one.
 */
export const maskPhone = (phone: string): string =>
  isNormalisedPhone(phone) ? `${phone.slice(0, 2)}****${phone.slice(-4)}` : '**********';
